import { TravelCurrencyCode } from '@graphql/generated';
import { CurrencyCode } from '@shared/hooks/useGeoLocale';

/**
 * Maps CurrencyCode from useGeoLocale to TravelCurrencyCode for GraphQL
 * Use Map to ensure key matching is case-sensitive and avoids object key coercion.
 */
const CURRENCY_CODE_TO_TRAVEL_CURRENCY_CODE = new Map<CurrencyCode, TravelCurrencyCode>([
  [CurrencyCode.USD, TravelCurrencyCode.USD],
  [CurrencyCode.EUR, TravelCurrencyCode.EUR],
  [CurrencyCode.GBP, TravelCurrencyCode.GBP],
  [CurrencyCode.CAD, TravelCurrencyCode.CAD],
  [CurrencyCode.AUD, TravelCurrencyCode.AUD],
  [CurrencyCode.JPY, TravelCurrencyCode.JPY],
]);

/**
 * Converts CurrencyCode from useGeoLocale to TravelCurrencyCode for GraphQL queries
 *
 * @param currency - Currency code from useGeoLocale
 * @returns TravelCurrencyCode for GraphQL, defaults to USD if not found
 *
 * @example
 * const travelCurrency = mapCurrencyCodeToTravelCurrencyCode(CurrencyCode.EUR);
 * // Returns TravelCurrencyCode.EUR
 */
export const mapCurrencyCodeToTravelCurrencyCode = (currency: CurrencyCode): TravelCurrencyCode => {
  return CURRENCY_CODE_TO_TRAVEL_CURRENCY_CODE.get(currency) ?? TravelCurrencyCode.USD;
};

/**
 * Maps TravelCurrencyCode back to CurrencyCode from useGeoLocale
 * This is the reverse mapping for cases where you need to go from GraphQL back to locale
 */
const TRAVEL_CURRENCY_CODE_TO_CURRENCY_CODE = new Map<TravelCurrencyCode, CurrencyCode>([
  [TravelCurrencyCode.USD, CurrencyCode.USD],
  [TravelCurrencyCode.EUR, CurrencyCode.EUR],
  [TravelCurrencyCode.GBP, CurrencyCode.GBP],
  [TravelCurrencyCode.CAD, CurrencyCode.CAD],
  [TravelCurrencyCode.AUD, CurrencyCode.AUD],
  [TravelCurrencyCode.JPY, CurrencyCode.JPY],
]);

/**
 * Converts TravelCurrencyCode back to CurrencyCode from useGeoLocale
 *
 * @param travelCurrency - TravelCurrencyCode from GraphQL
 * @returns CurrencyCode for useGeoLocale, defaults to USD if not found
 *
 * @example
 * const localeCurrency = mapTravelCurrencyCodeToCurrencyCode(TravelCurrencyCode.EUR);
 * // Returns CurrencyCode.EUR
 */
export const mapTravelCurrencyCodeToCurrencyCode = (travelCurrency: TravelCurrencyCode): CurrencyCode => {
  return TRAVEL_CURRENCY_CODE_TO_CURRENCY_CODE.get(travelCurrency) ?? CurrencyCode.USD;
};

/**
 * Type guard to check if a value is a valid TravelCurrencyCode
 *
 * @param value - The value to check
 * @returns True if the value is a valid TravelCurrencyCode, false otherwise
 *
 * @example
 * const isValid = isTravelCurrencyCode('USD'); // true
 * const isInvalid = isTravelCurrencyCode('INVALID'); // false
 */
export const isTravelCurrencyCode = (value: unknown): value is TravelCurrencyCode => {
  return typeof value === 'string' && (Object.values(TravelCurrencyCode) as string[]).includes(value);
};

/**
 * Validates and returns the first valid TravelCurrencyCode from a list of values, or a default
 *
 * @param valuesAndDefault - Spread array of values to check, with the last value being the default
 * @returns The first valid TravelCurrencyCode found, or the default if none are valid
 *
 * @example
 * const currency = validTravelCurrencyCode('INVALID', 'USD', TravelCurrencyCode.EUR);
 * // Returns TravelCurrencyCode.USD
 *
 * @example
 * const currency = validTravelCurrencyCode('INVALID', 'INVALID2', TravelCurrencyCode.EUR);
 * // Returns TravelCurrencyCode.EUR (default)
 */
export function validTravelCurrencyCode<T extends string>(...valuesAndDefault: [...Maybe<T>[], TravelCurrencyCode]): TravelCurrencyCode {
  const values = valuesAndDefault.slice(0, -1);
  const defaultValue = valuesAndDefault[valuesAndDefault.length - 1] as TravelCurrencyCode;
  for (const value of values) {
    if (value != null && isTravelCurrencyCode(value)) {
      return value as TravelCurrencyCode;
    }
  }
  return defaultValue;
}
